/* eslint-disable react/prop-types */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Field, reduxForm, change, submit, reset } from 'redux-form';
import { Badge, Button, Col, Form, FormGroup, Label, Row } from 'reactstrap';

import { isRequired } from '../../../../utils/validators';
import {
  clearMaintenanceServiceOrderDailyActivities,
  getDriverBusAssignments,
  getMyMaintenanceServiceOrderDailyActivities,
} from '../../../../actions';

import Alert from '../../../common/informative/Alert';
import DriverSelect from '../../../common/forms/select/DriverSelect';
import FormFooter from '../../../common/forms/FormFooter';
import FormItem from '../../../common/forms/FormItem';
import Loader from '../../../common/Loader';
import RegisteredBusSelect from '../../../common/forms/select/RegisteredBusSelect';
import DatePicker from '../../../common/forms/input/DatePicker';
import { INT_DATE_FORMAT } from '../../../../config/locale';
import { tzNormalizeDate } from '../../../../utils/date';

import MaintenanceChecklistAdditionalActivityForm, {
  MAINTENANCE_CHECKLIST_ADDITIONAL_ACTIVITY_FORM_NAME,
} from './MaintenanceChecklistAdditionalActivityForm';
import Modal from '../../../common/modal/Modal';
import { Table } from '../../../common/Table';

const FORM_NAME = 'MaintenanceServiceOrderDailyChecklistForm';

const toJSArray = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  if (Immutable.List.isList(value)) return value.toJS();

  return [];
};

const getDriverOptionsFromAssignments = (drivers) => {
  const driverList = Immutable.Map.isMap(drivers)
    ? drivers.get('content')
    : drivers;

  return toJSArray(driverList)
    .flatMap(({ driverAssignmentDateList = [] }) => driverAssignmentDateList)
    .filter(({ driver }) => driver)
    .map(({ id, driverId, driver, driverTypeId }) => ({
      value: driverId || driver.driverId,
      label: driver.driverName,
      code: driver.driverIdDocumentNumber,
      driverTypeId,
      assignmentId: id,
      raw: driver,
    }));
};

const getActivityName = (activity) =>
  activity.activityDescription ||
  activity.description ||
  activity.name ||
  activity.activityName ||
  'Actividad';

const getOptionLabel = (value) => {
  if (!value) return '-';

  if (typeof value === 'string' || typeof value === 'number') return value;

  if (Immutable.Map.isMap(value)) {
    return (
      value.get('label') ||
      value.get('name') ||
      value.get('description') ||
      value.get('activityDescription') ||
      '-'
    );
  }

  return (
    value.label ||
    value.name ||
    value.description ||
    value.activityDescription ||
    '-'
  );
};

const getOptionLabels = (value) => {
  if (!value) return '-';

  const list = toJSArray(value);

  if (!list.length) return '-';

  return list.map((item) => getOptionLabel(item)).join(', ');
};

const buildTempId = () => `${Date.now()}-${Math.random()}`;

const normalizeAdditionalOccurrences = (occurrences) =>
  toJSArray(occurrences).map((occurrence) => ({
    ...occurrence,
    tempId: occurrence.tempId || occurrence.id || buildTempId(),
  }));

export const MaintenanceServiceOrderDailyChecklistForm = ({
  handleSubmit,
  onSubmit,
  dispatchChange,
  dispatchSubmit,
  dispatchReset,
  dispatchGetDriverBusAssignments,
  dispatchGetMyMaintenanceServiceOrderDailyActivities,
  dispatchClearMaintenanceServiceOrderDailyActivities,
  drivers,
  dailyActivities,
  loadingDailyActivities,
  initialValues,
  loading,
}) => {
  const [registeredBus, setRegisteredBus] = useState(
    initialValues && initialValues.registeredBusId,
  );

  const [serviceDate, setServiceDate] = useState(
    initialValues && initialValues.date
      ? tzNormalizeDate({
          date: initialValues.date,
          format: INT_DATE_FORMAT,
        })
      : null,
  );

  const [useOtherDriver, setUseOtherDriver] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const [additionalOccurrences, setAdditionalOccurrences] = useState(
    initialValues && initialValues.additionalOccurrences
      ? normalizeAdditionalOccurrences(initialValues.additionalOccurrences)
      : [],
  );

  const [showAdditionalOccurrenceModal, setShowAdditionalOccurrenceModal] =
    useState(false);

  const [selectedAdditionalOccurrence, setSelectedAdditionalOccurrence] =
    useState(null);

  const driverOptions = useMemo(
    () => getDriverOptionsFromAssignments(drivers),
    [drivers],
  );

  const dailyActivitiesList = useMemo(
    () => toJSArray(dailyActivities),
    [dailyActivities],
  );

  const additionalOccurrenceTableData = useMemo(
    () =>
      additionalOccurrences.map((occurrence) => ({
        ...occurrence,
        maintenanceWarningTypeLabel: getOptionLabel(
          occurrence.maintenanceWarningType,
        ),
        maintenanceFailModeLabel: getOptionLabel(
          occurrence.maintenanceFailModeId,
        ),
        maintenanceFailModeCategoryLabel: getOptionLabel(
          occurrence.maintenanceFailModeCategoryId,
        ),
        maintenanceFailModeActivityListLabel: getOptionLabels(
          occurrence.maintenanceFailModeActivityList,
        ),
        priorityLabel: getOptionLabel(occurrence.priority),
        recurrenceLabel: getOptionLabel(occurrence.recurrence),
        commentsLabel: occurrence.comments || '-',
      })),
    [additionalOccurrences],
  );

  const selectAssignedDriver = (driver) => {
    setSelectedDriver(driver);
    dispatchChange(FORM_NAME, 'driverId', driver);
  };

  const loadDailyActivities = (busOption, dateValue) => {
    if (!busOption || !busOption.value || !dateValue) return;

    dispatchGetMyMaintenanceServiceOrderDailyActivities({
      registeredBusId: busOption.value,
      date: dateValue,
    });
  };

  useEffect(
    () => () => {
      dispatchClearMaintenanceServiceOrderDailyActivities();
      dispatchChange(FORM_NAME, 'driverId', null);
    },
    [],
  );

  useEffect(() => {
    if (useOtherDriver) return;

    if (driverOptions.length === 0) {
      setSelectedDriver(null);
      dispatchChange(FORM_NAME, 'driverId', null);
      return;
    }

    const selectedExists =
      selectedDriver &&
      driverOptions.some((driver) => driver.value === selectedDriver.value);

    if (!selectedExists) {
      selectAssignedDriver(driverOptions[0]);
    }
  }, [driverOptions, useOtherDriver]);

  const onRegisteredBusChange = (bus) => {
    setRegisteredBus(bus);
    setSelectedDriver(null);
    setUseOtherDriver(false);

    dispatchChange(FORM_NAME, 'driverId', null);

    if (!bus || !bus.value) {
      dispatchClearMaintenanceServiceOrderDailyActivities();
      return;
    }

    dispatchGetDriverBusAssignments({
      query: `companyBusId:${bus.label}`,
    });

    loadDailyActivities(bus, serviceDate);
  };

  const onServiceDateChange = (event, value) => {
    const backendDate = tzNormalizeDate({
      date: value,
      format: INT_DATE_FORMAT,
    });

    setServiceDate(backendDate);
    loadDailyActivities(registeredBus, backendDate);
  };

  const onUseOtherDriverChange = ({ target: { checked } }) => {
    setUseOtherDriver(checked);
    setSelectedDriver(null);
    dispatchChange(FORM_NAME, 'driverId', null);

    if (!checked && driverOptions.length > 0) {
      selectAssignedDriver(driverOptions[0]);
    }
  };

  const openAdditionalOccurrenceModal = () => {
    dispatchReset(MAINTENANCE_CHECKLIST_ADDITIONAL_ACTIVITY_FORM_NAME);
    setShowAdditionalOccurrenceModal(true);
  };

  const closeAdditionalOccurrenceModal = () => {
    setShowAdditionalOccurrenceModal(false);
    dispatchReset(MAINTENANCE_CHECKLIST_ADDITIONAL_ACTIVITY_FORM_NAME);
  };

  const onSubmitAdditionalOccurrence = (values) => {
    const newOccurrence = {
      ...values,
      tempId: buildTempId(),
    };

    setAdditionalOccurrences((current) => [...current, newOccurrence]);
    closeAdditionalOccurrenceModal();
  };

  const removeAdditionalOccurrence = (tempId) => {
    setAdditionalOccurrences((current) =>
      current.filter((occurrence) => occurrence.tempId !== tempId),
    );
  };

  const renderAdditionalOccurrenceDetail = () => {
    if (!selectedAdditionalOccurrence) return null;

    return (
      <div>
        <p>
          <strong>Tipo de ocurrencia:</strong>{' '}
          {getOptionLabel(selectedAdditionalOccurrence.maintenanceWarningType)}
        </p>

        <p>
          <strong>Modo de falla:</strong>{' '}
          {getOptionLabel(selectedAdditionalOccurrence.maintenanceFailModeId)}
        </p>

        <p>
          <strong>Categoría:</strong>{' '}
          {getOptionLabel(
            selectedAdditionalOccurrence.maintenanceFailModeCategoryId,
          )}
        </p>

        <p>
          <strong>Actividades:</strong>{' '}
          {getOptionLabels(
            selectedAdditionalOccurrence.maintenanceFailModeActivityList,
          )}
        </p>

        <p>
          <strong>Prioridad:</strong>{' '}
          {getOptionLabel(selectedAdditionalOccurrence.priority)}
        </p>

        <p>
          <strong>Reincidencia:</strong>{' '}
          {getOptionLabel(selectedAdditionalOccurrence.recurrence)}
        </p>

        <p>
          <strong>Comentario:</strong>{' '}
          {selectedAdditionalOccurrence.comments || '-'}
        </p>
      </div>
    );
  };

  const additionalOccurrenceColumns = useMemo(
    () => [
      {
        Header: 'Tipo',
        accessor: 'maintenanceWarningTypeLabel',
        className: 'text-center',
      },
      {
        Header: 'Modo de Falla',
        accessor: 'maintenanceFailModeLabel',
        className: 'text-center',
      },
      {
        Header: 'Categoría',
        accessor: 'maintenanceFailModeCategoryLabel',
        className: 'text-center',
      },
      {
        Header: 'Actividades',
        accessor: 'maintenanceFailModeActivityListLabel',
        className: 'text-center',
      },
      {
        Header: 'Prioridad',
        accessor: 'priorityLabel',
        className: 'text-center',
      },
      {
        Header: 'Comentario',
        accessor: 'commentsLabel',
        className: 'text-center',
      },
      {
        Header: 'Acciones',
        id: 'actions',
        className: 'text-center',
        filterable: false,
        sortable: false,
        notNavigateToModel: true,
        Cell: ({ original }) => (
          <>
            <Button
              type="button"
              color="info"
              outline
              size="sm"
              className="mr-2"
              onClick={() => setSelectedAdditionalOccurrence(original)}
            >
              Ver detalle
            </Button>

            <Button
              type="button"
              color="danger"
              outline
              size="sm"
              onClick={() => removeAdditionalOccurrence(original.tempId)}
            >
              <i className="fa fa-trash" />
            </Button>
          </>
        ),
      },
    ],
    [],
  );

  const renderDriverSelector = () => {
    if (!registeredBus) {
      return (
        <Alert
          type="info"
          message="Seleccione un bus para cargar los conductores asignados."
        />
      );
    }

    return (
      <>
        <Row className="mx-n1">
          {driverOptions.length === 0 && !useOtherDriver && (
            <Col xs="12">
              <Alert
                type="warning"
                message="No se encontraron conductores asignados para el bus seleccionado."
              />
            </Col>
          )}

          {!useOtherDriver &&
            driverOptions.map((driver) => {
              const active =
                selectedDriver && selectedDriver.value === driver.value;

              return (
                <Col
                  xs="12"
                  sm="12"
                  md="6"
                  lg="6"
                  xl="4"
                  key={driver.value}
                  className="px-1"
                >
                  <button
                    type="button"
                    className={`w-100 text-left mb-2 p-3 border rounded bg-white ${
                      active ? 'border-primary' : ''
                    }`}
                    style={{
                      minHeight: '82px',
                      whiteSpace: 'normal',
                    }}
                    onClick={() => selectAssignedDriver(driver)}
                  >
                    <div className="d-flex align-items-center">
                      <div className="mr-3 flex-shrink-0">
                        <i
                          className={`fa fa-user fa-2x ${
                            active ? 'text-primary' : 'text-muted'
                          }`}
                        />
                      </div>

                      <div
                        className="flex-grow-1"
                        style={{
                          minWidth: 0,
                          overflowWrap: 'break-word',
                        }}
                      >
                        <strong className="d-block">{driver.label}</strong>

                        {driver.code && (
                          <div className="text-muted">{driver.code}</div>
                        )}
                      </div>

                      <div className="ml-2 flex-shrink-0">
                        <i
                          className={
                            active
                              ? 'fa fa-check-circle text-primary'
                              : 'fa fa-circle-o text-muted'
                          }
                        />
                      </div>
                    </div>
                  </button>
                </Col>
              );
            })}
        </Row>

        <FormGroup check className="mt-2 mb-0">
          <Label check className="d-flex align-items-center flex-wrap">
            <input
              type="checkbox"
              checked={useOtherDriver}
              onChange={onUseOtherDriverChange}
              className="mr-2"
            />
            <span>Seleccionar otro conductor</span>
          </Label>
        </FormGroup>

        {useOtherDriver && (
          <FormGroup row className="mt-3">
            <FormItem label="Conductor alternativo" required>
              <Field
                name="driverId"
                component={DriverSelect}
                validate={[isRequired]}
                onlyActive
              />
            </FormItem>
          </FormGroup>
        )}

        <small className="text-muted d-block mt-2">
          El conductor se carga automáticamente según el bus seleccionado. Puede
          elegir una alternativa si es necesario.
        </small>
      </>
    );
  };

  const renderDailyActivities = () => {
    if (!registeredBus || !serviceDate) {
      return (
        <Alert
          type="info"
          message="Seleccione un bus y una fecha para cargar las actividades."
        />
      );
    }

    if (loadingDailyActivities) {
      return <Loader />;
    }

    if (dailyActivitiesList.length === 0) {
      return (
        <Alert
          type="info"
          message="No hay actividades asignadas para el bus y fecha seleccionados."
        />
      );
    }

    return (
      <Row>
        {dailyActivitiesList.map((activity, index) => (
          <Col
            xs="12"
            sm="6"
            md="4"
            lg="3"
            key={activity.id || activity.activityId || index}
          >
            <div className="border rounded p-3 mb-2 bg-white">
              <div className="d-flex align-items-center">
                <div className="mr-3 text-primary flex-shrink-0">
                  <i className="fa fa-wrench fa-2x" />
                </div>

                <strong
                  style={{
                    minWidth: 0,
                    overflowWrap: 'break-word',
                  }}
                >
                  {getActivityName(activity)}
                </strong>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    );
  };

  const renderAdditionalOccurrences = () => (
    <div className="border rounded p-3 mb-3 bg-white">
      <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
        <h3 className="mb-0">Ocurrencias Adicionales</h3>

        <Button
          type="button"
          color="success"
          outline
          onClick={openAdditionalOccurrenceModal}
        >
          <i className="fa fa-plus mr-1" />
          Agregar
        </Button>
      </div>

      {additionalOccurrences.length === 0 ? (
        <Alert
          type="info"
          message="No se han agregado ocurrencias adicionales."
        />
      ) : (
        <Table
          data={additionalOccurrenceTableData}
          columns={additionalOccurrenceColumns}
          manual={false}
          filterable={false}
          showPagination={additionalOccurrences.length > 5}
          defaultPageSize={5}
          keyField="tempId"
        />
      )}
    </div>
  );

  const onHandleSubmit = (formValues) => {
    const driverValue = formValues.driverId || selectedDriver;
    const busValue = formValues.registeredBusId;

    const backendDate = tzNormalizeDate({
      date: formValues.date,
      format: INT_DATE_FORMAT,
    });

    onSubmit({
      ...formValues,
      date: backendDate,
      registeredBusId: busValue && busValue.value ? busValue.value : busValue,
      driverId:
        driverValue && driverValue.value ? driverValue.value : driverValue,
      useOtherDriver,
      additionalOccurrences,
      activities: dailyActivitiesList,
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Form onSubmit={handleSubmit(onHandleSubmit)}>
      <div className="border rounded p-3 mb-3 bg-white">
        <Row>
          <Col xs="12" md="6" lg="3">
            <FormGroup>
              <Label>Número de Bus</Label>
              <Field
                name="registeredBusId"
                component={RegisteredBusSelect}
                onChange={onRegisteredBusChange}
                validate={[isRequired]}
                onlyActive
              />
            </FormGroup>
          </Col>

          <Col xs="12" md="6" lg="3">
            <FormGroup>
              <Label>Fecha del Servicio</Label>
              <Field
                name="date"
                component={DatePicker}
                validate={[isRequired]}
                onChange={onServiceDateChange}
              />
            </FormGroup>
          </Col>

          <Col xs="12" lg="6">
            <Label>
              Conductor asignado{' '}
              <small className="text-muted">
                cargado automáticamente según el bus
              </small>
            </Label>

            {renderDriverSelector()}
          </Col>
        </Row>
      </div>

      <div className="border rounded p-3 mb-3 bg-white">
        <h3 className="mb-3">
          Actividades asignadas a la Orden de Servicio{' '}
          <Badge color="primary" pill>
            Solo informativo
          </Badge>
        </h3>

        {renderDailyActivities()}
      </div>

      {renderAdditionalOccurrences()}

      <Modal
        show={showAdditionalOccurrenceModal}
        title="Agregar ocurrencia adicional"
        body={
          <MaintenanceChecklistAdditionalActivityForm
            onSubmit={onSubmitAdditionalOccurrence}
            registeredBusId={registeredBus}
            requiredField
            showFooter={false}
          />
        }
        onClickClose={closeAdditionalOccurrenceModal}
        onClickCancel={closeAdditionalOccurrenceModal}
        onClickConfirm={() =>
          dispatchSubmit(MAINTENANCE_CHECKLIST_ADDITIONAL_ACTIVITY_FORM_NAME)
        }
        confirmButtonText="Agregar"
        cancelButtonText="Cancelar"
      />

      <Modal
        show={!!selectedAdditionalOccurrence}
        title="Detalle de ocurrencia adicional"
        body={renderAdditionalOccurrenceDetail()}
        onClickClose={() => setSelectedAdditionalOccurrence(null)}
      />

      <FormFooter
        saveButtonDisabled={loading}
        saveButtonIcon={loading ? 'fa fa-spinner fa-spin' : 'fa fa-save'}
      />
    </Form>
  );
};

MaintenanceServiceOrderDailyChecklistForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  dispatchSubmit: PropTypes.func.isRequired,
  dispatchReset: PropTypes.func.isRequired,
  dispatchGetDriverBusAssignments: PropTypes.func.isRequired,
  dispatchGetMyMaintenanceServiceOrderDailyActivities:
    PropTypes.func.isRequired,
  dispatchClearMaintenanceServiceOrderDailyActivities:
    PropTypes.func.isRequired,
  drivers: PropTypes.oneOfType([
    PropTypes.instanceOf(Immutable.Map),
    PropTypes.instanceOf(Immutable.List),
    PropTypes.array,
  ]).isRequired,
  dailyActivities: PropTypes.oneOfType([
    PropTypes.instanceOf(Immutable.List),
    PropTypes.array,
  ]),
  loadingDailyActivities: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({
    registeredBusId: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
    date: PropTypes.string,
    additionalOccurrences: PropTypes.array,
  }),
  loading: PropTypes.bool,
};

MaintenanceServiceOrderDailyChecklistForm.defaultProps = {
  dailyActivities: Immutable.List(),
  initialValues: null,
  loading: false,
};

const mapStateToProps = (state) => ({
  drivers: state.TrafficUnit.DriverBusAssignment.getIn(['all', 'content']),

  dailyActivities:
    state.MechanicalMaintenanceUnit.MaintenanceServiceOrder.getIn([
      'current',
      'dailyActivities',
    ]),

  loadingDailyActivities:
    !state.MechanicalMaintenanceUnit.MaintenanceServiceOrder.getIn([
      'current',
      'activity',
    ]).isEmpty(),

  loading: !state.MechanicalMaintenanceUnit.MaintenanceChecklist.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

const mapDispatchToProps = {
  dispatchChange: change,
  dispatchSubmit: submit,
  dispatchReset: reset,
  dispatchGetDriverBusAssignments: getDriverBusAssignments,
  dispatchGetMyMaintenanceServiceOrderDailyActivities:
    getMyMaintenanceServiceOrderDailyActivities,
  dispatchClearMaintenanceServiceOrderDailyActivities:
    clearMaintenanceServiceOrderDailyActivities,
};

const formComponent = reduxForm({
  form: FORM_NAME,
})(MaintenanceServiceOrderDailyChecklistForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
